import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, ControlSelect, useChartTheme, listJoin, PHONE_CHART_MARGIN,
} from '../../components/insights/InsightPage';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatNumber } from '../../utils/format';

const BLANK = '(blank)';

const CustomStatusLabels = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [minCompanies, setMinCompanies] = useState(2);
  const [limit, setLimit] = useState(25);
  const { data, loading, error, reload } = useInsightData(
    () => insightsService.customStatusLabels(minCompanies, limit),
    [minCompanies, limit]
  );
  const rows = (data || []).map((r) => ({ ...r, label: r.label || BLANK }));

  const top = rows[0];
  // A blank label is not something a team invented, so it stays out of the post
  const topThree = rows.filter((r) => r.label !== BLANK).slice(0, 3).map((r) => `"${r.label}"`);
  const copy = topThree.length
    ? `Beyond "office" and "home", the whereabouts ${topThree.length === 1 ? 'status' : 'statuses'} teams invent most: ${listJoin(topThree)}. Every workplace has its own language. 🗣️`
    : '';

  const controls = (
    <>
      <ControlSelect id="customLabelsMinimum" label="Used by at least" value={minCompanies} onChange={(e) => setMinCompanies(Number(e.target.value))}>
        {[2, 3, 5, 10].map((v) => <option key={v} value={v}>{v} companies</option>)}
      </ControlSelect>
      <ControlSelect id="customLabelsLimit" label="Show top" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
        {[10, 25, 50].map((v) => <option key={v} value={v}>{v}</option>)}
      </ControlSelect>
    </>
  );

  return (
    <InsightPage
      title="What teams call working elsewhere"
      description="The most common custom whereabouts statuses companies create."
      controls={controls}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={rows.length === 0}
      emptyMessage={`No custom status is used by ${minCompanies} or more companies`}
    >
      <StatRow>
        <StatCard xs={4} label="Distinct labels shown" value={formatNumber(rows.length)} />
        <StatCard xs={4} label="Most common label" value={top?.label || EMPTY} variant="primary" />
        <StatCard xs={4} label="Companies using it" value={formatNumber(top?.companiesUsing ?? 0)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Custom statuses by number of companies using them</h5></Card.Header>
        <Card.Body>
          {/* 36px a row leaves room for a label that wraps onto two lines, so every label can show */}
          <ResponsiveContainer width="100%" height={Math.max(360, rows.length * 36)}>
            <BarChart data={rows} layout="vertical" margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 20, bottom: 10 }}>
              {/* Bars run sideways, so the grid lines do too */}
              <CartesianGrid {...t.grid} vertical horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...t.xAxis} />
              <YAxis type="category" dataKey="label" interval={0} {...t.yAxis} width={isMobile ? 124 : 140} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="companiesUsing" name="Companies" fill={t.colors.emphasis} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default CustomStatusLabels;
